/**
 * Global time utilities for LA Mirage wedding platform
 * All times are displayed in Indian Standard Time (IST)
 */

export const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * Format time string (HH:MM) to 12-hour format with IST suffix
 */
export function formatTimeIST(time: string): string {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm} IST`;
}

/**
 * Format date to IST with proper Indian date format
 */
export function formatDateIST(date: Date | string | null | undefined): string {
  if (!date) return 'No date provided';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if the date is valid
  if (!dateObj || isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }
  
  return dateObj.toLocaleDateString('en-IN', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long'
  });
}

/**
 * Format datetime to IST with both date and time
 */
export function formatDateTimeIST(date: Date | string | null | undefined): string {
  if (!date) return 'No date provided';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if the date is valid
  if (!dateObj || isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }
  
  return dateObj.toLocaleString('en-IN', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }) + ' IST';
}

/**
 * Get current IST time
 */
export function getCurrentIST(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: IST_TIMEZONE }));
}

/**
 * Convert any date to IST timezone
 */
export function convertToIST(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Date(dateObj.toLocaleString('en-US', { timeZone: IST_TIMEZONE }));
}

/**
 * Create IST date object from date components
 */
export function createISTDate(year: number, month: number, day: number): Date {
  const date = new Date(year, month, day);
  return convertToIST(date);
}

/**
 * Get month and year in IST timezone
 */
export function getMonthYearIST(date: Date | string): { month: number; year: number } {
  const istDate = convertToIST(date);
  return {
    month: istDate.getMonth(),
    year: istDate.getFullYear()
  };
}

/**
 * Format month/year for calendar headers in IST
 */
export function formatMonthYearIST(date: Date): string {
  return date.toLocaleDateString('en-IN', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: 'long'
  });
}

/**
 * Get day of week in IST
 */
export function getDayOfWeekIST(date: Date): string {
  return date.toLocaleDateString('en-IN', {
    timeZone: IST_TIMEZONE,
    weekday: 'long'
  });
}